import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  addLocalDays,
  loadRestaurantDemoState,
  localDateString,
  saveRestaurantDemoState,
  type RestaurantDemoProduct,
  type RestaurantDemoState,
} from "./restaurant-sandbox";
import {
  loadRestaurantDemoV5State,
  modifierGroupsForProduct,
  visualForProduct,
  type RestaurantDemoModifierGroup,
  type RestaurantDemoV5State,
} from "./restaurant-demo-v5-state";
import {
  addRestaurantCustomerCartLine,
  buildRestaurantCustomerCartLine,
  changeRestaurantCustomerCartQuantity,
  commitRestaurantCustomerOrder,
  commitRestaurantCustomerReservation,
  restaurantCustomerCartTotalMinor,
  type RestaurantCustomerCartLine,
  type RestaurantCustomerFulfillment,
} from "./restaurant-customer-ordering";
import {
  RESTAURANT_CUSTOMER_RESET_EVENT,
  restaurantCustomerResetStorageKey,
} from "./restaurant-customer-conversation";
import { restaurantCustomerPath, type RestaurantCustomerSurface } from "./restaurant-customer-routing";
import { RestaurantCustomerWebchat } from "./RestaurantCustomerWebchat";
import "./restaurant-customer-experience.css";

const money = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 });
const formatMoney = (minor: number) => money.format(minor / 100);

function normalized(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("es").trim();
}

function customerIntentKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID().replaceAll("-", "");
  return `${Date.now()}${Math.random().toString(36).slice(2)}`;
}

function customerInitials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "CD";
}

function defaultSelections(groups: readonly RestaurantDemoModifierGroup[]): Readonly<Record<string, readonly string[]>> {
  const result: Record<string, readonly string[]> = {};
  for (const group of groups) result[group.id] = group.minSelections > 0 ? Object.freeze(group.options.slice(0, group.minSelections).map((option) => option.id)) : Object.freeze([]);
  return Object.freeze(result);
}

function currentConfiguredPrice(product: RestaurantDemoProduct, groups: readonly RestaurantDemoModifierGroup[], selections: Readonly<Record<string, readonly string[]>>): number {
  let total = product.priceMinor;
  for (const group of groups) {
    for (const optionId of selections[group.id] ?? []) total += group.options.find((option) => option.id === optionId)?.priceDeltaMinor ?? 0;
  }
  return total;
}

function PublicHeader({
  businessSlug,
  businessName,
  surface,
  navigate,
}: {
  readonly businessSlug: string;
  readonly businessName: string;
  readonly surface: RestaurantCustomerSurface;
  readonly navigate: (path: string) => void;
}) {
  const items: readonly { readonly id: RestaurantCustomerSurface; readonly label: string }[] = [
    { id: "home", label: "Inicio" },
    { id: "menu", label: "Carta" },
    { id: "order", label: "Pedir" },
    { id: "reservation", label: "Reservar" },
    { id: "assistant", label: "Asistente" },
  ];
  return <header className="rcx-topbar">
    <button type="button" className="rcx-brand" onClick={() => navigate(restaurantCustomerPath(businessSlug))}>
      <span className="rcx-brand-mark">{customerInitials(businessName)}</span>
      <span><strong>{businessName}</strong><small>Experiencia cliente</small></span>
    </button>
    <nav className="rcx-nav" aria-label="Navegación del restaurante">
      {items.map((item) => <button type="button" key={item.id} className={surface === item.id ? "active" : ""} onClick={() => navigate(restaurantCustomerPath(businessSlug, item.id))}>{item.label}</button>)}
    </nav>
    <button className="rcx-secondary rcx-admin-link" type="button" onClick={() => navigate(`${restaurantCustomerPath(businessSlug)}&view=admin`)}>Administración ↗</button>
  </header>;
}

function DemoNote() {
  return <div className="rcx-demo-note"><span><b>Demo comercial conectada.</b> Carta, pedidos y reservas leen el mismo sandbox que Administración.</span><span>Sin acciones externas</span></div>;
}

function Home({
  state,
  v5,
  businessSlug,
  businessName,
  navigate,
}: {
  readonly state: RestaurantDemoState;
  readonly v5: RestaurantDemoV5State;
  readonly businessSlug: string;
  readonly businessName: string;
  readonly navigate: (path: string) => void;
}) {
  const featured = state.products.filter((product) => product.active && product.featured).slice(0, 3);
  const available = state.products.filter((product) => product.active && product.available).length;
  return <>
    <section className="rcx-hero">
      <div>
        <span className="rcx-eyebrow">Restaurant experience</span>
        <h1>{businessName}</h1>
        <p>Descubrí la carta, personalizá tu pedido, reservá una mesa o preguntale al asistente. Todo lo que ves está conectado con la misma operación que usa el negocio.</p>
        <div className="rcx-actions">
          <button className="rcx-primary" type="button" onClick={() => navigate(restaurantCustomerPath(businessSlug, "menu"))}>Ver la carta →</button>
          <button className="rcx-secondary" type="button" onClick={() => navigate(restaurantCustomerPath(businessSlug, "order"))}>Hacer un pedido</button>
          <button className="rcx-secondary" type="button" onClick={() => navigate(restaurantCustomerPath(businessSlug, "reservation"))}>Reservar mesa</button>
        </div>
      </div>
      <div className="rcx-hero-card" aria-label="Especialidad del restaurante">
        <div><span className="rcx-eyebrow" style={{ color: "#fff" }}>Carta viva</span><strong>{available} productos disponibles</strong></div>
        <div className="plate" aria-hidden="true">🍕</div>
        <span>Los cambios de Administración se reflejan acá.</span>
      </div>
    </section>
    <section className="rcx-proof-strip" aria-label="Servicios disponibles">
      <div><small>Carta</small><strong>Precios y disponibilidad en vivo</strong></div>
      <div><small>Pedidos</small><strong>Retiro o delivery</strong></div>
      <div><small>Atención</small><strong>Asistente + handoff humano</strong></div>
    </section>
    <section>
      <div className="rcx-section-heading"><div><span className="rcx-eyebrow">Para empezar</span><h2>Favoritos de la casa</h2></div><p>Estos productos vienen del mismo catálogo editable que ve el equipo.</p></div>
      <div className="rcx-product-grid">{featured.map((product) => <ProductCard key={product.id} product={product} v5={v5} actionLabel="Ver carta" onAction={() => navigate(restaurantCustomerPath(businessSlug, "menu"))} />)}</div>
    </section>
  </>;
}

function ProductCard({
  product,
  v5,
  actionLabel,
  onAction,
}: {
  readonly product: RestaurantDemoProduct;
  readonly v5: RestaurantDemoV5State;
  readonly actionLabel: string;
  readonly onAction: () => void;
}) {
  const visual = visualForProduct(v5, product);
  const groups = modifierGroupsForProduct(v5, product.id);
  const minPrice = product.priceMinor + groups.reduce((sum, group) => sum + group.options.slice(0, group.minSelections).reduce((inner, option) => inner + option.priceDeltaMinor, 0), 0);
  return <article className="rcx-product-card">
    <div className="rcx-product-visual">{visual.imageUrl ? <img src={visual.imageUrl} alt="" /> : <span aria-hidden="true">{visual.accent}</span>}</div>
    <div className="rcx-product-body">
      <div className="rcx-product-meta"><span>{product.category}</span><span className={`rcx-availability${product.available ? "" : " off"}`}>{product.available ? "Disponible" : "Agotado"}</span></div>
      <h3>{product.name}</h3><p>{product.description}</p>
      <div className="rcx-product-footer"><strong>{groups.length ? `Desde ${formatMoney(minPrice)}` : formatMoney(product.priceMinor)}</strong><button type="button" onClick={onAction} disabled={!product.available}>{actionLabel}</button></div>
    </div>
  </article>;
}

function Configurator({
  product,
  v5,
  ordering,
  onClose,
  onAdd,
}: {
  readonly product: RestaurantDemoProduct;
  readonly v5: RestaurantDemoV5State;
  readonly ordering: boolean;
  readonly onClose: () => void;
  readonly onAdd: (selections: Readonly<Record<string, readonly string[]>>) => void;
}) {
  const groups = modifierGroupsForProduct(v5, product.id);
  const [selections, setSelections] = useState<Readonly<Record<string, readonly string[]>>>(() => defaultSelections(groups));
  const currentPrice = currentConfiguredPrice(product, groups, selections);
  function toggle(group: RestaurantDemoModifierGroup, optionId: string) {
    const selected = selections[group.id] ?? [];
    const active = selected.includes(optionId);
    let next: readonly string[];
    if (active) next = selected.filter((id) => id !== optionId);
    else if (group.maxSelections === 1) next = [optionId];
    else next = [...selected, optionId].slice(0, group.maxSelections);
    setSelections(Object.freeze({ ...selections, [group.id]: Object.freeze(next) }));
  }
  const valid = groups.every((group) => (selections[group.id]?.length ?? 0) >= group.minSelections && (selections[group.id]?.length ?? 0) <= group.maxSelections);
  return <div className="rcx-config-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="rcx-config" role="dialog" aria-modal="true" aria-labelledby="rcx-config-title">
      <header><div><span className="rcx-eyebrow">Personalizar</span><h2 id="rcx-config-title">{product.name}</h2><p>{product.description}</p></div><button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>
      {groups.length === 0 ? <p>Este producto se pide tal cual.</p> : groups.map((group) => <fieldset className="rcx-config-group" key={group.id}>
        <legend>{group.name}<small>{group.required ? "Obligatorio" : "Opcional"} · elegí {group.minSelections === group.maxSelections ? group.minSelections : `${group.minSelections}–${group.maxSelections}`}</small></legend>
        <div className="rcx-option-grid">{group.options.map((option) => { const active = selections[group.id]?.includes(option.id) ?? false; return <button type="button" className={`rcx-option${active ? " active" : ""}`} aria-pressed={active} key={option.id} onClick={() => toggle(group, option.id)}><span>{active ? "✓ " : ""}{option.name}</span><strong>{option.priceDeltaMinor ? `+${formatMoney(option.priceDeltaMinor)}` : "Incluido"}</strong></button>; })}</div>
      </fieldset>)}
      <footer className="rcx-config-footer"><strong>{formatMoney(currentPrice)}</strong>{ordering ? <button className="rcx-primary" type="button" disabled={!valid || !product.available} onClick={() => onAdd(selections)}>{product.available ? "Agregar al pedido" : "No disponible"}</button> : <button className="rcx-secondary" type="button" onClick={onClose}>Cerrar</button>}</footer>
    </section>
  </div>;
}

function MenuAndOrder({
  state,
  v5,
  setState,
  ordering,
}: {
  readonly state: RestaurantDemoState;
  readonly v5: RestaurantDemoV5State;
  readonly setState: (state: RestaurantDemoState) => void;
  readonly ordering: boolean;
}) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState<RestaurantDemoProduct | null>(null);
  const [cart, setCart] = useState<readonly RestaurantCustomerCartLine[]>([]);
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [fulfillment, setFulfillment] = useState<RestaurantCustomerFulfillment>("pickup");
  const [address, setAddress] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const orderIntentRef = useRef<string | null>(null);
  const submitLockRef = useRef(false);
  const categories = state.categories.filter((item) => item.active).sort((a, b) => a.sortOrder - b.sortOrder);
  const products = state.products.filter((product) => product.active)
    .filter((product) => category === "all" || product.category === category)
    .filter((product) => !search || normalized(`${product.name} ${product.description} ${product.tags.join(" ")}`).includes(normalized(search)))
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const totalMinor = restaurantCustomerCartTotalMinor(cart);

  function invalidateOrderIntent() { orderIntentRef.current = null; }
  function replaceCart(updater: (current: readonly RestaurantCustomerCartLine[]) => readonly RestaurantCustomerCartLine[]) {
    invalidateOrderIntent();
    setCart(updater);
  }

  function addConfigured(selections: Readonly<Record<string, readonly string[]>>) {
    if (!selectedProduct) return;
    const result = buildRestaurantCustomerCartLine({ state, v5, productId: selectedProduct.id, selections });
    if (!result.ok || !result.line) { setError(result.detail ?? "No pudimos agregar ese producto."); return; }
    replaceCart((current) => addRestaurantCustomerCartLine(current, result.line!));
    setSelectedProduct(null); setError(null); setNotice(`${result.line.name} agregado al pedido.`);
  }

  function confirmOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitLockRef.current) return;
    submitLockRef.current = true; setSubmitting(true); setError(null); setNotice(null);
    const idempotencyKey = orderIntentRef.current ?? customerIntentKey();
    orderIntentRef.current = idempotencyKey;
    try {
      const result = commitRestaurantCustomerOrder({ state, v5, idempotencyKey, customerName, phone, fulfillment, deliveryAddress: address, cart });
      setState(result.state); setCart([]); setAddress(""); orderIntentRef.current = null;
      setNotice(`Pedido ${result.value.id.replace("ord-web-", "#").slice(0, 10)} confirmado. Ya apareció en cocina.`);
    } catch (cause) {
      orderIntentRef.current = null;
      const code = cause instanceof Error ? cause.message : "customer_order_failed";
      setError(code === "product_unavailable" ? "Un producto dejó de estar disponible. Actualizá el pedido." : code === "delivery_address_required" ? "Ingresá una dirección para delivery." : "No pudimos confirmar el pedido. Revisá la carta actual e intentá de nuevo.");
    } finally {
      submitLockRef.current = false; setSubmitting(false);
    }
  }

  return <>
    <div className="rcx-section-heading"><div><span className="rcx-eyebrow">{ordering ? "Pedido online" : "Carta digital"}</span><h1>{ordering ? "Armá tu pedido" : "Nuestra carta"}</h1></div><p>{ordering ? "Elegí variantes y extras. El precio se recalcula desde la carta actual antes de confirmar." : "Precios, disponibilidad y opciones conectados con Administración."}</p></div>
    {notice ? <div className="rcx-notice" role="status">{notice}</div> : null}{error ? <div className="rcx-error" role="alert">{error}</div> : null}
    <div className="rcx-menu-toolbar"><input className="rcx-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar pizza, entrada, postre…" aria-label="Buscar en la carta" /><div className="rcx-category-list"><button className={category === "all" ? "active" : ""} type="button" onClick={() => setCategory("all")}>Todo</button>{categories.map((item) => <button type="button" className={category === item.name ? "active" : ""} key={item.id} onClick={() => setCategory(item.name)}>{item.name}</button>)}</div></div>
    <div className={ordering ? "rcx-menu-layout" : ""}><div className="rcx-product-grid">{products.length ? products.map((product) => <ProductCard key={product.id} product={product} v5={v5} actionLabel={product.available ? (modifierGroupsForProduct(v5, product.id).length ? "Ver opciones" : ordering ? "Agregar" : "Ver detalle") : "Agotado"} onAction={() => product.available && setSelectedProduct(product)} />) : <div className="rcx-empty">No encontramos productos con ese filtro.</div>}</div>
      {ordering ? <aside className="rcx-cart" aria-label="Tu pedido"><header><div><small>Tu pedido</small><strong>{cart.reduce((sum, line) => sum + line.quantity, 0)} ítems</strong></div><strong>{formatMoney(totalMinor)}</strong></header><div className="rcx-cart-lines">{cart.length ? cart.map((line) => <div className="rcx-cart-line" key={line.key}><div><strong>{line.name}</strong><small>{(line.modifiers ?? []).map((modifier) => modifier.label).join(" · ") || "Sin extras"}</small><small>{formatMoney(line.unitPriceMinor + (line.modifiers ?? []).reduce((sum, modifier) => sum + modifier.priceDeltaMinor, 0))} c/u</small></div><div className="rcx-cart-qty"><button type="button" aria-label={`Quitar una unidad de ${line.name}`} onClick={() => replaceCart((current) => changeRestaurantCustomerCartQuantity(current, line.key, -1))}>−</button><span>{line.quantity}</span><button type="button" aria-label={`Agregar una unidad de ${line.name}`} onClick={() => replaceCart((current) => changeRestaurantCustomerCartQuantity(current, line.key, 1))}>+</button></div></div>) : <p>Elegí algo de la carta para empezar.</p>}</div><div className="rcx-cart-total"><span>Total</span><strong>{formatMoney(totalMinor)}</strong></div><form className="rcx-form" onSubmit={confirmOrder}><label>Nombre<input value={customerName} onChange={(event) => { invalidateOrderIntent(); setCustomerName(event.target.value); }} required placeholder="Tu nombre" /></label><label>Teléfono <small>opcional</small><input value={phone} onChange={(event) => { invalidateOrderIntent(); setPhone(event.target.value); }} placeholder="351…" /></label><fieldset><legend>¿Cómo lo recibís?</legend><div className="rcx-fulfillment"><button type="button" className={fulfillment === "pickup" ? "active" : ""} onClick={() => { invalidateOrderIntent(); setFulfillment("pickup"); }}>Retiro</button><button type="button" className={fulfillment === "delivery" ? "active" : ""} onClick={() => { invalidateOrderIntent(); setFulfillment("delivery"); }}>Delivery</button></div></fieldset>{fulfillment === "delivery" ? <label>Dirección<input value={address} onChange={(event) => { invalidateOrderIntent(); setAddress(event.target.value); }} required placeholder="Calle, número y referencia" /></label> : null}<button className="rcx-primary" disabled={submitting || !cart.length || !customerName.trim()} type="submit">{submitting ? "Confirmando…" : `Confirmar · ${formatMoney(totalMinor)}`}</button></form></aside> : null}
    </div>
    {selectedProduct ? <Configurator product={selectedProduct} v5={v5} ordering={ordering} onClose={() => setSelectedProduct(null)} onAdd={addConfigured} /> : null}
  </>;
}

function Reservation({ state, setState }: { readonly state: RestaurantDemoState; readonly setState: (state: RestaurantDemoState) => void }) {
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [partySize, setPartySize] = useState(2);
  const [date, setDate] = useState(addLocalDays(localDateString(), 1));
  const [time, setTime] = useState("21:00");
  const [area, setArea] = useState("all");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const reservationIntentRef = useRef<string | null>(null);
  const submitLockRef = useRef(false);
  function invalidateReservationIntent() { reservationIntentRef.current = null; }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitLockRef.current) return;
    submitLockRef.current = true; setSubmitting(true); setError(null); setNotice(null);
    const idempotencyKey = reservationIntentRef.current ?? customerIntentKey();
    reservationIntentRef.current = idempotencyKey;
    try {
      const result = commitRestaurantCustomerReservation({ state, idempotencyKey, customerName, phone, partySize, date, time, area: area === "all" ? null : area });
      setState(result.state); reservationIntentRef.current = null;
      const table = result.state.tables.find((candidate) => candidate.id === result.value.tableId);
      setNotice(`Reserva confirmada para ${date} a las ${time}${table ? ` · ${table.label}` : ""}.`);
    } catch (cause) {
      reservationIntentRef.current = null;
      const code = cause instanceof Error ? cause.message : "reservation_failed";
      setError(code === "reservation_unavailable" ? "No encontramos una mesa compatible en ese horario. Probá otra hora o cantidad de personas." : "No pudimos confirmar la reserva. Revisá los datos e intentá de nuevo.");
    } finally {
      submitLockRef.current = false; setSubmitting(false);
    }
  }
  const activeTables = state.tables.filter((table) => table.active);
  return <><div className="rcx-section-heading"><div><span className="rcx-eyebrow">Reservas</span><h1>Tu mesa, sin vueltas</h1></div><p>La disponibilidad se valida contra el mismo Timeline de Mesas que ve el equipo.</p></div>{notice ? <div className="rcx-notice" role="status">{notice}</div> : null}{error ? <div className="rcx-error" role="alert">{error}</div> : null}<div className="rcx-reservation-grid"><section className="rcx-panel"><form className="rcx-form" onSubmit={submit}><label>Nombre<input value={customerName} onChange={(event) => { invalidateReservationIntent(); setCustomerName(event.target.value); }} required /></label><label>Teléfono <small>opcional</small><input value={phone} onChange={(event) => { invalidateReservationIntent(); setPhone(event.target.value); }} /></label><label>Personas<input type="number" min="1" max="30" value={partySize} onChange={(event) => { invalidateReservationIntent(); setPartySize(Number(event.target.value)); }} required /></label><label>Fecha<input type="date" min={localDateString()} value={date} onChange={(event) => { invalidateReservationIntent(); setDate(event.target.value); }} required /></label><label>Hora<input type="time" value={time} onChange={(event) => { invalidateReservationIntent(); setTime(event.target.value); }} required /></label><label>Preferencia<select value={area} onChange={(event) => { invalidateReservationIntent(); setArea(event.target.value); }}><option value="all">Cualquier sector</option>{state.areas.filter((item) => item.active).map((item) => <option value={item.name} key={item.id}>{item.name}</option>)}</select></label><button className="rcx-primary" type="submit" disabled={submitting}>{submitting ? "Confirmando…" : "Buscar y confirmar mesa"}</button></form></section><aside className="rcx-reservation-promise"><span className="rcx-eyebrow">Disponibilidad real</span><strong>{activeTables.length} mesas</strong><p>ClickDialog compara capacidad, fecha, horario, duración y reservas existentes antes de asignar una mesa.</p><p>Si no hay capacidad compatible, no crea una reserva parcial ni inventa disponibilidad.</p></aside></div></>;
}

export function RestaurantCustomerExperience({
  businessSlug,
  businessName,
  surface,
  navigate,
}: {
  readonly businessSlug: string;
  readonly businessName: string;
  readonly surface: RestaurantCustomerSurface;
  readonly navigate: (path: string) => void;
}) {
  const [state, setRawState] = useState<RestaurantDemoState>(() => loadRestaurantDemoState(businessSlug, businessName));
  const [v5, setV5] = useState<RestaurantDemoV5State>(() => loadRestaurantDemoV5State(businessSlug, loadRestaurantDemoState(businessSlug, businessName)));
  const [chatOpen, setChatOpen] = useState(false);
  const [resetRevision, setResetRevision] = useState(0);
  const reload = useCallback(() => { const next = loadRestaurantDemoState(businessSlug, businessName); setRawState(next); setV5(loadRestaurantDemoV5State(businessSlug, next)); }, [businessSlug, businessName]);

  useEffect(() => {
    const resetKey = restaurantCustomerResetStorageKey(businessSlug);
    const onStorage = (event: StorageEvent) => {
      reload();
      if (event.key === resetKey) { setChatOpen(false); setResetRevision((value) => value + 1); }
    };
    const onReset = (event: Event) => {
      const slug = event instanceof CustomEvent && typeof event.detail?.slug === "string" ? event.detail.slug : null;
      if (slug !== businessSlug) return;
      reload(); setChatOpen(false); setResetRevision((value) => value + 1);
    };
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") setChatOpen(false); };
    window.addEventListener("focus", reload);
    window.addEventListener("storage", onStorage);
    window.addEventListener(RESTAURANT_CUSTOMER_RESET_EVENT, onReset);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("focus", reload);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(RESTAURANT_CUSTOMER_RESET_EVENT, onReset);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [businessSlug, reload]);

  function setState(next: RestaurantDemoState) { const normalizedState = Object.freeze({ ...next, businessName }); saveRestaurantDemoState(businessSlug, normalizedState); setRawState(normalizedState); }
  function setAgentState(nextState: RestaurantDemoState, nextV5: RestaurantDemoV5State) { setRawState(Object.freeze({ ...nextState, businessName })); setV5(nextV5); }

  const activeSurface = surface;
  return <div className="restaurant-customer-shell">
    <PublicHeader businessSlug={businessSlug} businessName={businessName} surface={activeSurface} navigate={navigate} />
    <main className="rcx-page" key={resetRevision}>
      <DemoNote />
      {activeSurface === "home" ? <Home state={state} v5={v5} businessSlug={businessSlug} businessName={businessName} navigate={navigate} /> : null}
      {activeSurface === "menu" ? <MenuAndOrder state={state} v5={v5} setState={setState} ordering={false} /> : null}
      {activeSurface === "order" ? <MenuAndOrder state={state} v5={v5} setState={setState} ordering /> : null}
      {activeSurface === "reservation" ? <Reservation state={state} setState={setState} /> : null}
      {activeSurface === "assistant" ? <div className="rcx-chat-stage"><div className="rcx-section-heading"><div><span className="rcx-eyebrow">Asistente</span><h1>Preguntá como cliente</h1></div><p>IA real con una allowlist distinta del agente del dueño. Carta, pedidos, reservas y handoff humano.</p></div><RestaurantCustomerWebchat businessSlug={businessSlug} businessName={businessName} state={state} v5={v5} onStateChange={setAgentState} focusComposer /></div> : null}
      <footer className="rcx-footer"><span>{businessName} · experiencia cliente demo</span><span>Carta, pedidos, reservas y atención conectados al Restaurant OS.</span></footer>
    </main>
    {activeSurface !== "assistant" ? <>
      <button className="rcx-floating-chat" type="button" aria-expanded={chatOpen} aria-controls="restaurant-customer-chat-drawer" onClick={() => setChatOpen((value) => !value)}>💬 {chatOpen ? "Cerrar" : "¿Necesitás ayuda?"}</button>
      {chatOpen ? <div className="rcx-chat-drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setChatOpen(false); }}><aside className="rcx-chat-drawer" id="restaurant-customer-chat-drawer" aria-label={`Chat con ${businessName}`}><div className="rcx-chat-drawer-toolbar"><span>Atención online</span><button type="button" aria-label="Cerrar asistente" onClick={() => setChatOpen(false)}>×</button></div><RestaurantCustomerWebchat businessSlug={businessSlug} businessName={businessName} state={state} v5={v5} onStateChange={setAgentState} focusComposer /></aside></div> : null}
    </> : null}
  </div>;
}
